import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Save, Trash2, X, Pencil, Users2, Calendar, ClipboardCheck,
  Clock, Store as StoreIcon, KeyRound, EyeOff, Eye, FileDown, Copy, ListChecks,
} from "lucide-react";
import {
  listEmployees, createEmployee, updateEmployee, deleteEmployee,
  listShifts, shiftSummary, createShift, updateShift, deleteShift,
  listTaskTemplates, createTaskTemplate, updateTaskTemplate, deleteTaskTemplate,
  listTimeEntries, listShiftTemplates, copyPreviousWeek,
  CADENCE_OPTIONS, WEEKDAY_OPTIONS,
} from "../../../lib/teamsSchedule";
import { listStores } from "../../../lib/emailTemplates";
import { ShiftTemplatesPanel, fmtHHMM } from "./ShiftTemplatesPanel";
import { exportSchedulePdf } from "./schedulePdf";

/* ------------------------------------------------------------------ */
/* utils                                                              */
/* ------------------------------------------------------------------ */
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
};
const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};
const toIsoZ = (localValue) => {
  // localValue: "2026-08-01T14:00" (from datetime-local input)
  if (!localValue) return "";
  try { return new Date(localValue).toISOString(); } catch { return ""; }
};
const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

const startOfWeekIso = (d = new Date()) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x.toISOString().slice(0, 10);
};

const CADENCE_LABEL = Object.fromEntries(CADENCE_OPTIONS.map((c) => [c.value, c.label]));
const WEEKDAY_LABEL = Object.fromEntries(WEEKDAY_OPTIONS.map((w) => [w.value, w.label]));

/* ------------------------------------------------------------------ */
/* Field wrapper                                                      */
/* ------------------------------------------------------------------ */
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

/* ------------------------------------------------------------------ */
/* EMPLOYEES PANE                                                     */
/* ------------------------------------------------------------------ */
const EMPTY_EMPLOYEE = { name: "", store_id: "", passcode: "", active: true };

const EmployeesPane = ({ stores, employees, refresh }) => {
  const [draft, setDraft] = useState(EMPTY_EMPLOYEE);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showPassDraft, setShowPassDraft] = useState(false);
  const [saving, setSaving] = useState(false);

  const startEdit = (emp) => {
    setEditingId(emp.id);
    setDraft({ name: emp.name, store_id: emp.store_id, passcode: "", active: emp.active !== false });
    setShowAdd(true);
  };

  const save = async () => {
    if (!draft.name.trim() || !draft.store_id) {
      toast.error("Name and store are required");
      return;
    }
    if (!editingId && !/^\d{4}$/.test(draft.passcode)) {
      toast.error("Passcode must be exactly 4 digits");
      return;
    }
    if (editingId && draft.passcode && !/^\d{4}$/.test(draft.passcode)) {
      toast.error("New passcode must be exactly 4 digits");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const patch = { name: draft.name, store_id: draft.store_id, active: draft.active };
        if (draft.passcode) patch.passcode = draft.passcode;
        await updateEmployee(editingId, patch);
        toast.success("Employee updated");
      } else {
        await createEmployee(draft);
        toast.success(`Employee added — passcode ${draft.passcode}`);
      }
      setDraft(EMPTY_EMPLOYEE); setEditingId(null); setShowAdd(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this employee and their shifts?")) return;
    try { await deleteEmployee(id); toast.success("Employee removed"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="employees-pane">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Store Employees</div>
          <div className="text-sm mt-0.5">{employees.length} on the roster</div>
        </div>
        <button
          onClick={() => { setEditingId(null); setDraft(EMPTY_EMPLOYEE); setShowAdd(true); }}
          className="btn-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm"
          data-testid="employee-add-btn"
        >
          <Plus size={13} /> Add employee
        </button>
      </div>

      <div className="card-swiss rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead className="bg-[color:var(--ups-card-alt)]">
            <tr>
              <Th>Name</Th><Th>Store</Th><Th>Passcode</Th><Th>Status</Th><Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-[color:var(--ups-subtext)]">No employees yet — add your first team member.</td></tr>
            )}
            {employees.map((e) => (
              <tr key={e.id} className="border-t border-[color:var(--ups-border)]">
                <td className="px-3 py-2 font-medium" data-testid={`employee-name-${e.id}`}>{e.name}</td>
                <td className="px-3 py-2 text-[color:var(--ups-subtext)]">{stores.find((s) => s.id === e.store_id)?.name || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">••••</span>
                    <span className="text-[10px] text-[color:var(--ups-subtext)]">(hidden — reset to change)</span>
                  </div>
                </td>
                <td className="px-3 py-2">
                  {e.active === false
                    ? <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Inactive</span>
                    : <span className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{color:"#16A34A"}}>Active</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => startEdit(e)} className="p-1 hover:bg-white/10 rounded-sm" data-testid={`employee-edit-${e.id}`}><Pencil size={13} /></button>
                  <button onClick={() => remove(e.id)} className="p-1 hover:bg-white/10 rounded-sm ml-1" data-testid={`employee-delete-${e.id}`}><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <Modal title={editingId ? "Edit employee" : "Add employee"} onClose={() => { setShowAdd(false); setEditingId(null); }}>
          <div className="grid grid-cols-1 gap-3">
            <Field label="Name">
              <input className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                data-testid="employee-name-input" />
            </Field>
            <Field label="Store">
              <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.store_id}
                onChange={(e) => setDraft({ ...draft, store_id: e.target.value })}
                data-testid="employee-store-select">
                <option value="">— Pick a store —</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label={editingId ? "New passcode (leave blank to keep current)" : "4-digit passcode"}>
              <div className="flex items-center gap-2">
                <input
                  type={showPassDraft ? "text" : "password"}
                  className="input-swiss w-full px-3 py-2 rounded-sm text-sm font-mono tracking-widest"
                  maxLength={4}
                  inputMode="numeric"
                  pattern="\d{4}"
                  value={draft.passcode}
                  onChange={(e) => setDraft({ ...draft, passcode: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  placeholder="0000"
                  data-testid="employee-passcode-input"
                />
                <button type="button" onClick={() => setShowPassDraft((v) => !v)}
                  className="p-2 rounded-sm border border-[color:var(--ups-border)]" aria-label="toggle passcode">
                  {showPassDraft ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <div className="mt-1 text-[10px] text-[color:var(--ups-subtext)]">
                Share this with the employee. They&apos;ll use it on the kiosk to clock in/out.
              </div>
            </Field>
            {editingId && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
                Active (uncheck to hide from kiosk without deleting)
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => { setShowAdd(false); setEditingId(null); }}
                className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border border-[color:var(--ups-border)]">
                Cancel
              </button>
              <button disabled={saving} onClick={save}
                className="btn-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm"
                data-testid="employee-save-btn">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {editingId ? "Save" : "Add employee"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* SCHEDULE PANE                                                      */
/* ------------------------------------------------------------------ */
const EMPTY_SHIFT = { store_id: "", employee_id: "", start_at: "", end_at: "", notes: "" };

const SchedulePane = ({ stores, employees, storeFilter, setStoreFilter }) => {
  const [weekStart, setWeekStart] = useState(startOfWeekIso());
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState({ totals_by_employee: {}, grand_total_hours: 0 });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("grid"); // 'grid' | 'list'
  const [draft, setDraft] = useState(EMPTY_SHIFT);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [copying, setCopying] = useState(false);

  const loadTemplates = () => listShiftTemplates().then(setTemplates).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + 6);
    return d.toISOString().slice(0, 10);
  }, [weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from_date: weekStart, to_date: weekEnd };
      if (storeFilter) params.store_id = storeFilter;
      const [s, sm] = await Promise.all([listShifts(params), shiftSummary(params)]);
      setShifts(s || []);
      setSummary(sm || { totals_by_employee: {}, grand_total_hours: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load schedule");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [weekStart, storeFilter]);

  const storeLabel = storeFilter ? (stores.find((s) => s.id === storeFilter)?.name || "Store") : "All stores";
  const weekLabel = `${new Date(weekStart + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })} — ${new Date(weekEnd + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

  const copyLastWeek = async () => {
    if (!window.confirm(`Replace all shifts for ${storeLabel} in the week of ${weekLabel} with a copy of the previous week?`)) return;
    setCopying(true);
    try {
      const res = await copyPreviousWeek({ week_start: weekStart, ...(storeFilter ? { store_id: storeFilter } : {}) });
      toast.success(`Copied ${res.copied} shift${res.copied === 1 ? "" : "s"} from last week (${res.replaced} replaced)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Copy failed"); }
    finally { setCopying(false); }
  };

  const applyTemplate = (id) => {
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) { setDraft({ ...draft, template_id: "" }); return; }
    const day = (draft.start_at || "").slice(0, 10);
    setDraft({ ...draft, template_id: id, start_at: `${day}T${tpl.start}`, end_at: `${day}T${tpl.end}` });
  };

  const shiftWeek = (delta) => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const openNew = (empId = "", dayIdx = 0) => {
    const day = new Date(weekStart + "T00:00:00");
    day.setDate(day.getDate() + (Number.isFinite(dayIdx) ? dayIdx : 0));
    day.setHours(9, 0, 0, 0);
    const end = new Date(day); end.setHours(17, 0, 0, 0);
    setEditingId(null);
    setDraft({
      store_id: storeFilter || stores[0]?.id || "",
      employee_id: empId || "",
      start_at: toLocalInput(day.toISOString()),
      end_at: toLocalInput(end.toISOString()),
      notes: "",
      template_id: "",
    });
    setShowModal(true);
  };

  const openEdit = (sh) => {
    setEditingId(sh.id);
    setDraft({
      store_id: sh.store_id, employee_id: sh.employee_id,
      start_at: toLocalInput(sh.start_at), end_at: toLocalInput(sh.end_at),
      notes: sh.notes || "", template_id: "",
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!draft.store_id || !draft.employee_id || !draft.start_at || !draft.end_at) {
      toast.error("All fields except notes are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        store_id: draft.store_id, employee_id: draft.employee_id,
        start_at: toIsoZ(draft.start_at), end_at: toIsoZ(draft.end_at),
        notes: draft.notes,
      };
      if (editingId) await updateShift(editingId, payload);
      else await createShift(payload);
      toast.success(editingId ? "Shift updated" : "Shift created");
      setShowModal(false); setEditingId(null); setDraft(EMPTY_SHIFT);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save shift");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this shift?")) return;
    try { await deleteShift(id); toast.success("Shift removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Compute draft hours for the modal (ms → hours, 2-decimal precision)
  const draftHours = useMemo(() => {
    if (!draft.start_at || !draft.end_at) return 0;
    const s = new Date(draft.start_at); const e = new Date(draft.end_at);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0;
    return Math.max(0, Math.round((e - s) / 3.6e6 * 100) / 100);
  }, [draft.start_at, draft.end_at]);

  const employeesInScope = storeFilter ? employees.filter((e) => e.store_id === storeFilter) : employees;
  const days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + i);
    return d;
  });

  return (
    <div className="space-y-4" data-testid="schedule-pane">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => shiftWeek(-1)} className="px-2 py-1 rounded-sm border border-[color:var(--ups-border)] text-sm" data-testid="schedule-prev-week">◀</button>
        <div className="text-sm font-medium" data-testid="schedule-week-label">
          {weekLabel}
        </div>
        <button onClick={() => shiftWeek(1)} className="px-2 py-1 rounded-sm border border-[color:var(--ups-border)] text-sm" data-testid="schedule-next-week">▶</button>
        <button onClick={() => setWeekStart(startOfWeekIso())} className="px-2 py-1 rounded-sm border border-[color:var(--ups-border)] text-xs uppercase tracking-widest">Today</button>
        <div className="flex-1" />
        <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)}
          className="input-swiss px-2 py-1.5 rounded-sm text-sm" data-testid="schedule-store-filter">
          <option value="">All stores</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="inline-flex rounded-sm border border-[color:var(--ups-border)] overflow-hidden">
          <button onClick={() => setView("grid")} className={`px-2 py-1 text-xs uppercase tracking-widest ${view==="grid" ? "bg-[color:var(--ups-brand)] text-black" : ""}`} data-testid="schedule-view-grid">Grid</button>
          <button onClick={() => setView("list")} className={`px-2 py-1 text-xs uppercase tracking-widest ${view==="list" ? "bg-[color:var(--ups-brand)] text-black" : ""}`} data-testid="schedule-view-list">List</button>
        </div>
        <button onClick={() => setShowTemplates((v) => !v)} className={`px-2 py-1.5 rounded-sm border border-[color:var(--ups-border)] text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 ${showTemplates ? "bg-[color:var(--ups-card-alt)]" : ""}`} data-testid="schedule-templates-toggle">
          <ListChecks size={13} /> Saved shifts
        </button>
        <button onClick={copyLastWeek} disabled={copying} className="px-2 py-1.5 rounded-sm border border-[color:var(--ups-border)] text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 disabled:opacity-50" data-testid="schedule-copy-week-btn">
          {copying ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />} Copy last week
        </button>
        <button onClick={() => exportSchedulePdf({ days, employees: employeesInScope, shifts, totals: summary.totals_by_employee, storeName: storeLabel, weekLabel })}
          className="px-2 py-1.5 rounded-sm border border-[color:var(--ups-border)] text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" data-testid="schedule-export-pdf-btn">
          <FileDown size={13} /> Export PDF
        </button>
        <button onClick={() => openNew()} className="btn-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm" data-testid="shift-add-btn">
          <Plus size={13} /> Shift
        </button>
      </div>

      {showTemplates && <ShiftTemplatesPanel templates={templates} reload={loadTemplates} />}

      {/* Hours summary */}
      <div className="card-swiss rounded-sm p-3">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Total scheduled this week</div>
          <div className="text-2xl font-bold" data-testid="schedule-grand-total">{summary.grand_total_hours.toFixed(2)} h</div>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.entries(summary.totals_by_employee).map(([eid, hrs]) => {
            const name = employees.find((e) => e.id === eid)?.name || "—";
            return <span key={eid} className="text-xs px-2 py-0.5 rounded-sm bg-[color:var(--ups-card-alt)] border border-[color:var(--ups-border)]" data-testid={`schedule-emp-total-${eid}`}>{name}: <b>{hrs}h</b></span>;
          })}
          {Object.keys(summary.totals_by_employee).length === 0 && <span className="text-xs text-[color:var(--ups-subtext)]">No shifts scheduled yet this week.</span>}
        </div>
      </div>

      {/* Grid view */}
      {view === "grid" && (
        <div className="card-swiss rounded-sm overflow-x-auto">
          {loading ? <div className="p-6 text-center"><Loader2 size={16} className="animate-spin inline" /></div> : (
            <table className="w-full text-xs min-w-[820px] table-fixed">
              <thead>
                <tr className="bg-[color:var(--ups-card-alt)]">
                  <Th className="w-[140px]">Employee</Th>
                  {days.map((d, i) => (
                    <Th key={i} className="text-center">
                      <div className="text-[10px] font-bold uppercase tracking-widest">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                      <div className="text-[10px] font-normal text-[color:var(--ups-subtext)]">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employeesInScope.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-[color:var(--ups-subtext)]">No employees {storeFilter ? "at this store" : "yet"}.</td></tr>
                )}
                {employeesInScope.map((emp) => (
                  <tr key={emp.id} className="border-t border-[color:var(--ups-border)]">
                    <td className="px-3 py-2 font-medium">{emp.name}</td>
                    {days.map((d, i) => {
                      const dayKey = d.toISOString().slice(0, 10);
                      const cellShifts = shifts.filter((s) => s.employee_id === emp.id && (s.start_at || "").slice(0, 10) === dayKey);
                      return (
                        <td key={i} className="px-1 py-1 align-top" data-testid={`schedule-cell-${emp.id}-${dayKey}`}>
                          {cellShifts.map((s) => (
                            <button key={s.id} onClick={() => openEdit(s)}
                              className="w-full text-left text-[10px] px-2 py-1 mb-1 rounded-sm bg-[color:var(--ups-brand)] text-black font-mono hover:opacity-80"
                              data-testid={`shift-${s.id}`}>
                              {fmtTime(s.start_at)}–{fmtTime(s.end_at)} <b>({s.hours}h)</b>
                            </button>
                          ))}
                          <button onClick={() => openNew(emp.id, i)} className="w-full text-[10px] py-0.5 rounded-sm border border-dashed border-[color:var(--ups-border)] text-[color:var(--ups-subtext)] hover:bg-white/5">
                            +
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* List view */}
      {view === "list" && (
        <div className="card-swiss rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--ups-card-alt)]">
              <tr><Th>Employee</Th><Th>Store</Th><Th>Start</Th><Th>End</Th><Th>Hours</Th><Th>Notes</Th><Th /></tr>
            </thead>
            <tbody>
              {shifts.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[color:var(--ups-subtext)]">No shifts this week.</td></tr>}
              {shifts.map((s) => {
                const emp = employees.find((e) => e.id === s.employee_id);
                const store = stores.find((st) => st.id === s.store_id);
                return (
                  <tr key={s.id} className="border-t border-[color:var(--ups-border)]" data-testid={`shift-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{emp?.name || "—"}</td>
                    <td className="px-3 py-2 text-[color:var(--ups-subtext)]">{store?.name || "—"}</td>
                    <td className="px-3 py-2 font-mono">{fmtDateTime(s.start_at)}</td>
                    <td className="px-3 py-2 font-mono">{fmtDateTime(s.end_at)}</td>
                    <td className="px-3 py-2 font-bold">{s.hours}h</td>
                    <td className="px-3 py-2 text-xs text-[color:var(--ups-subtext)]">{s.notes || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => openEdit(s)} className="p-1 hover:bg-white/10 rounded-sm"><Pencil size={13} /></button>
                      <button onClick={() => remove(s.id)} className="p-1 hover:bg-white/10 rounded-sm ml-1"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title={editingId ? "Edit shift" : "New shift"} onClose={() => setShowModal(false)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Store">
              <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.store_id}
                onChange={(e) => setDraft({ ...draft, store_id: e.target.value })}
                data-testid="shift-store-select">
                <option value="">—</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Employee">
              <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.employee_id}
                onChange={(e) => setDraft({ ...draft, employee_id: e.target.value })}
                data-testid="shift-employee-select">
                <option value="">—</option>
                {employees.filter((emp) => !draft.store_id || emp.store_id === draft.store_id).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Shift">
                <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.template_id || ""}
                  onChange={(e) => applyTemplate(e.target.value)} data-testid="shift-template-select">
                  <option value="">Custom times</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {fmtHHMM(t.start)} – {fmtHHMM(t.end)}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Starts">
              <input type="datetime-local" className="input-swiss w-full px-3 py-2 rounded-sm text-sm"
                value={draft.start_at} onChange={(e) => {
                  const tpl = templates.find((x) => x.id === draft.template_id);
                  const day = e.target.value.slice(0, 10);
                  // Keep the saved shift's times when only the date changes.
                  if (tpl && e.target.value.slice(11) === tpl.start) setDraft({ ...draft, start_at: e.target.value, end_at: `${day}T${tpl.end}` });
                  else setDraft({ ...draft, start_at: e.target.value, template_id: "" });
                }}
                data-testid="shift-start-input" />
            </Field>
            <Field label="Ends">
              <input type="datetime-local" className="input-swiss w-full px-3 py-2 rounded-sm text-sm"
                value={draft.end_at} onChange={(e) => setDraft({ ...draft, end_at: e.target.value, template_id: "" })}
                data-testid="shift-end-input" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notes (optional)">
                <input className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
              </Field>
            </div>
            <div className="sm:col-span-2 flex items-baseline justify-between pt-1">
              <div className="text-xs text-[color:var(--ups-subtext)]">Total for this shift</div>
              <div className="text-2xl font-bold" data-testid="shift-draft-hours">{draftHours.toFixed(2)} h</div>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
              {editingId && (
                <button onClick={() => remove(editingId)} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border border-red-500/50 text-red-500 flex items-center gap-1.5"><Trash2 size={13}/> Delete</button>
              )}
              <div className="flex-1" />
              <button onClick={() => setShowModal(false)} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border border-[color:var(--ups-border)]">Cancel</button>
              <button disabled={saving} onClick={save} className="btn-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm" data-testid="shift-save-btn">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* TASKS PANE                                                         */
/* ------------------------------------------------------------------ */
const EMPTY_TASK = { store_id: "", title: "", description: "", cadence: "daily",
  weekday: 0, day_of_month: 1, biweekly_anchor: "", adhoc_date: "",
  assignee_employee_id: "", active: true };

const TasksPane = ({ stores, employees, storeFilter, setStoreFilter }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(EMPTY_TASK);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (storeFilter) params.store_id = storeFilter;
      setTasks(await listTaskTemplates(params));
    } catch (e) { toast.error("Failed to load tasks"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [storeFilter]);

  const openNew = () => {
    setEditingId(null);
    setDraft({ ...EMPTY_TASK, store_id: storeFilter || stores[0]?.id || "" });
    setShowModal(true);
  };
  const openEdit = (t) => {
    setEditingId(t.id);
    setDraft({
      store_id: t.store_id, title: t.title || "", description: t.description || "",
      cadence: t.cadence || "daily",
      weekday: t.weekday ?? 0, day_of_month: t.day_of_month ?? 1,
      biweekly_anchor: (t.biweekly_anchor || "").slice(0, 10),
      adhoc_date: (t.adhoc_date || "").slice(0, 10),
      assignee_employee_id: t.assignee_employee_id || "",
      active: t.active !== false,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!draft.store_id || !draft.title.trim()) {
      toast.error("Store and title are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        store_id: draft.store_id,
        title: draft.title.trim(),
        description: draft.description,
        cadence: draft.cadence,
        active: draft.active,
        assignee_employee_id: draft.assignee_employee_id || null,
      };
      if (draft.cadence === "weekly" || draft.cadence === "biweekly") payload.weekday = Number(draft.weekday);
      if (draft.cadence === "biweekly") payload.biweekly_anchor = draft.biweekly_anchor;
      if (draft.cadence === "monthly") payload.day_of_month = Number(draft.day_of_month);
      if (draft.cadence === "adhoc") payload.adhoc_date = draft.adhoc_date;
      if (editingId) await updateTaskTemplate(editingId, payload);
      else await createTaskTemplate(payload);
      toast.success(editingId ? "Task updated" : "Task added");
      setShowModal(false); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save task");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this task template?")) return;
    try { await deleteTaskTemplate(id); toast.success("Task deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cadenceSummary = (t) => {
    if (t.cadence === "weekly") return `Weekly · ${WEEKDAY_LABEL[t.weekday]}`;
    if (t.cadence === "biweekly") return `Bi-Weekly · ${WEEKDAY_LABEL[t.weekday]} (anchor ${t.biweekly_anchor || "—"})`;
    if (t.cadence === "monthly") return `Monthly · day ${t.day_of_month}`;
    if (t.cadence === "adhoc") return `One-off · ${t.adhoc_date || "—"}`;
    return "Daily";
  };

  return (
    <div className="space-y-4" data-testid="tasks-pane">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Task Templates</div>
          <div className="text-sm mt-0.5">Employees see these on the kiosk when clocking in.</div>
        </div>
        <div className="flex-1" />
        <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className="input-swiss px-2 py-1.5 rounded-sm text-sm" data-testid="tasks-store-filter">
          <option value="">All stores</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={openNew} className="btn-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm" data-testid="task-add-btn">
          <Plus size={13} /> Task
        </button>
      </div>

      <div className="card-swiss rounded-sm overflow-hidden">
        {loading ? <div className="p-6 text-center"><Loader2 size={16} className="animate-spin inline" /></div> : (
          <table className="w-full text-sm" data-testid="tasks-table">
            <thead className="bg-[color:var(--ups-card-alt)]">
              <tr><Th>Title</Th><Th>Store</Th><Th>Cadence</Th><Th>Assignee</Th><Th /></tr>
            </thead>
            <tbody>
              {tasks.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[color:var(--ups-subtext)]">No tasks yet. Add a task template to build the daily/weekly checklist.</td></tr>}
              {tasks.map((t) => (
                <tr key={t.id} className="border-t border-[color:var(--ups-border)]" data-testid={`task-row-${t.id}`}>
                  <td className="px-3 py-2 font-medium">
                    {t.title}
                    {t.description && <div className="text-xs text-[color:var(--ups-subtext)] mt-0.5">{t.description}</div>}
                  </td>
                  <td className="px-3 py-2 text-[color:var(--ups-subtext)]">{stores.find((s) => s.id === t.store_id)?.name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{cadenceSummary(t)}</td>
                  <td className="px-3 py-2 text-xs">{t.assignee_employee_id ? (employees.find((e) => e.id === t.assignee_employee_id)?.name || "—") : <span className="text-[color:var(--ups-subtext)]">Store-wide</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openEdit(t)} className="p-1 hover:bg-white/10 rounded-sm"><Pencil size={13} /></button>
                    <button onClick={() => remove(t.id)} className="p-1 hover:bg-white/10 rounded-sm ml-1"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <Modal title={editingId ? "Edit task" : "New task"} onClose={() => setShowModal(false)}>
          <div className="grid grid-cols-1 gap-3">
            <Field label="Title">
              <input className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                data-testid="task-title-input" />
            </Field>
            <Field label="Description (optional)">
              <textarea className="input-swiss w-full px-3 py-2 rounded-sm text-sm" rows={2} value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Store">
                <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.store_id}
                  onChange={(e) => setDraft({ ...draft, store_id: e.target.value })}
                  data-testid="task-store-select">
                  <option value="">—</option>
                  {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Cadence">
                <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.cadence}
                  onChange={(e) => setDraft({ ...draft, cadence: e.target.value })}
                  data-testid="task-cadence-select">
                  {CADENCE_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              {(draft.cadence === "weekly" || draft.cadence === "biweekly") && (
                <Field label="Weekday">
                  <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.weekday}
                    onChange={(e) => setDraft({ ...draft, weekday: Number(e.target.value) })}>
                    {WEEKDAY_OPTIONS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                  </select>
                </Field>
              )}
              {draft.cadence === "biweekly" && (
                <Field label="Anchor date (a Monday in an 'on' week)">
                  <input type="date" className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.biweekly_anchor}
                    onChange={(e) => setDraft({ ...draft, biweekly_anchor: e.target.value })} />
                </Field>
              )}
              {draft.cadence === "monthly" && (
                <Field label="Day of month">
                  <input type="number" min={1} max={31} className="input-swiss w-full px-3 py-2 rounded-sm text-sm font-mono" value={draft.day_of_month}
                    onChange={(e) => setDraft({ ...draft, day_of_month: e.target.value })} />
                </Field>
              )}
              {draft.cadence === "adhoc" && (
                <Field label="Due on">
                  <input type="date" className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.adhoc_date}
                    onChange={(e) => setDraft({ ...draft, adhoc_date: e.target.value })} />
                </Field>
              )}
              <Field label="Assignee (empty = store-wide)">
                <select className="input-swiss w-full px-3 py-2 rounded-sm text-sm" value={draft.assignee_employee_id}
                  onChange={(e) => setDraft({ ...draft, assignee_employee_id: e.target.value })}>
                  <option value="">Store-wide (everyone at this store)</option>
                  {employees.filter((e) => !draft.store_id || e.store_id === draft.store_id).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowModal(false)} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border border-[color:var(--ups-border)]">Cancel</button>
              <button disabled={saving} onClick={save} className="btn-primary px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm" data-testid="task-save-btn">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* TIME LOG PANE                                                      */
/* ------------------------------------------------------------------ */
const TimeLogPane = ({ stores, employees, storeFilter, setStoreFilter }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (storeFilter) params.store_id = storeFilter;
      setEntries(await listTimeEntries(params));
    } catch (e) { toast.error("Failed to load time log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [storeFilter]);

  return (
    <div className="space-y-4" data-testid="timelog-pane">
      <div className="flex items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Kiosk Time Log</div>
        <div className="flex-1" />
        <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className="input-swiss px-2 py-1.5 rounded-sm text-sm">
          <option value="">All stores</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="card-swiss rounded-sm overflow-hidden">
        {loading ? <div className="p-6 text-center"><Loader2 size={16} className="animate-spin inline" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--ups-card-alt)]">
              <tr><Th>Employee</Th><Th>Store</Th><Th>Clock In</Th><Th>Clock Out</Th><Th>Hours</Th><Th>Tasks</Th></tr>
            </thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[color:var(--ups-subtext)]">No time entries yet.</td></tr>}
              {entries.map((en) => (
                <tr key={en.id} className="border-t border-[color:var(--ups-border)]" data-testid={`timelog-${en.id}`}>
                  <td className="px-3 py-2 font-medium">{en.employee_name || employees.find((e) => e.id === en.employee_id)?.name || "—"}</td>
                  <td className="px-3 py-2 text-[color:var(--ups-subtext)]">{stores.find((s) => s.id === en.store_id)?.name || "—"}</td>
                  <td className="px-3 py-2 font-mono">{fmtDateTime(en.clock_in_at)}</td>
                  <td className="px-3 py-2 font-mono">{en.clock_out_at ? fmtDateTime(en.clock_out_at) : <span className="text-green-500 font-bold">Active</span>}</td>
                  <td className="px-3 py-2 font-bold">{en.hours_worked != null ? `${en.hours_worked}h` : "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {(en.task_results || []).length === 0 ? <span className="text-[color:var(--ups-subtext)]">—</span> : (
                      <div className="space-y-0.5">
                        {(en.task_results || []).map((tr, i) => (
                          <div key={i} className={tr.status === "done" ? "text-green-500" : "text-yellow-500"}>
                            {tr.status === "done" ? "✔" : "○"} {tr.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Shared UI                                                          */
/* ------------------------------------------------------------------ */
const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] ${className}`}>{children}</th>
);

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
    <div className="card-swiss rounded-sm max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold uppercase tracking-[0.18em]">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded"><X size={16} /></button>
      </div>
      {children}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Main tab                                                           */
/* ------------------------------------------------------------------ */
const TeamsScheduleTab = () => {
  const [pane, setPane] = useState("employees");
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [storeFilter, setStoreFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [s, e] = await Promise.all([listStores({ include_archived: false }), listEmployees({ include_archived: false })]);
      setStores(Array.isArray(s) ? s : s?.items || []);
      setEmployees(e || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load — is your role 'owner'?");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const storeNum = (name) => (name || "").match(/(?<!\d)\d{4}(?!\d)/)?.[0];
  const kioskLink = (num) => `${window.location.origin}/kiosk/store${num}`;

  const PANES = [
    { id: "employees", label: "Employees", icon: Users2 },
    { id: "schedule", label: "Schedule", icon: Calendar },
    { id: "tasks", label: "Tasks", icon: ClipboardCheck },
    { id: "timelog", label: "Time Log", icon: Clock },
  ];

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div className="space-y-6" data-testid="teams-schedule-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Teams & Schedule</h2>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">Employees clock in from the kiosk — you see everything here.</p>
        </div>
        {stores.length > 0 && (
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Kiosk link for stores</div>
            <div className="space-y-1 mt-1">
              {stores.slice(0, 3).map((s) => {
                const num = storeNum(s.name);
                return (
                  <div key={s.id} className="text-xs font-mono flex items-center gap-2 justify-end">
                    <span className="text-[color:var(--ups-subtext)]">{s.name}:</span>
                    {num ? (
                      <>
                        <a href={kioskLink(num)} target="_blank" rel="noopener noreferrer"
                           className="underline hover:text-[color:var(--ups-brand)]" data-testid={`kiosk-link-${s.id}`}>
                          /kiosk/store{num}
                        </a>
                        <button onClick={() => { navigator.clipboard.writeText(kioskLink(num)); toast.success("Copied"); }}
                          className="text-[10px] px-1 rounded-sm border border-[color:var(--ups-border)]" data-testid={`kiosk-link-copy-${s.id}`}>Copy</button>
                      </>
                    ) : (
                      <span className="text-amber-500" data-testid={`kiosk-link-missing-${s.id}`}>Add the 4-digit store number to the store name</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[color:var(--ups-border)] pb-1" role="tablist">
        {PANES.map((p) => (
          <button key={p.id} onClick={() => setPane(p.id)}
            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 ${pane===p.id ? "bg-[color:var(--ups-brand)] text-black" : "hover:bg-white/5"}`}
            data-testid={`teams-pane-${p.id}`}>
            <p.icon size={13} /> {p.label}
          </button>
        ))}
      </div>

      {pane === "employees" && <EmployeesPane stores={stores} employees={employees} refresh={refresh} />}
      {pane === "schedule" && <SchedulePane stores={stores} employees={employees} storeFilter={storeFilter} setStoreFilter={setStoreFilter} />}
      {pane === "tasks" && <TasksPane stores={stores} employees={employees} storeFilter={storeFilter} setStoreFilter={setStoreFilter} />}
      {pane === "timelog" && <TimeLogPane stores={stores} employees={employees} storeFilter={storeFilter} setStoreFilter={setStoreFilter} />}
    </div>
  );
};

export default TeamsScheduleTab;
